import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { PRODUCT_IMAGE_PLACEHOLDER } from '@/constants/images';

/**
 * The listing gallery (`WEB-004`, `specs/product/listing-content-panel`,
 * `AUT-231`). Split into a main image and a thumbnail strip because the
 * card puts them in two different regions: the image is edge-to-edge at
 * the top of the card, the thumbnails sit inside the padded body with the
 * title and the bullets (`design-input/listing-content-panel/
 * prototype-extracted.md § Panel card`; `AUT-297`). A single component
 * rendering both cannot express that -- it would have to own the card's
 * padding, and the title below it would inherit the wrong offset.
 *
 * The history modal drives the same two pieces from the same hook
 * (`AUT-301`), which is what makes its thumbnails selectable at all.
 *
 * Deliberately does not import `resolveProductImage` or anything from
 * `@/assets/products` -- this screen renders the stored URL and falls back
 * to the one shared neutral placeholder
 * (`components/product.md § Real images on this screen, and the fence
 * around it`).
 */

const MIN_SLOTS = 5;

export interface ListingGallerySelection {
  selectedIndex: number;
  select: (index: number) => void;
  mainImage: string;
}

/**
 * `padToMinimumSlots` is the panel's rule, not the modal's: the panel pads
 * to five slots for a consistent PDP feel, the modal shows the real
 * gallery length (`prototype-extracted.md § History modal`).
 */
export function useListingGallery(images: string[]): ListingGallerySelection {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // A refetch always hands back a new array reference, even when the URLs
  // are unchanged, so keying the reset on the joined contents (rather than
  // the reference) avoids snapping the selection back to 0 on every
  // unrelated re-render.
  const imagesKey = images.join('|');
  useEffect(() => {
    setSelectedIndex(0);
  }, [imagesKey]);

  return {
    selectedIndex,
    select: setSelectedIndex,
    mainImage: images[selectedIndex] ?? PRODUCT_IMAGE_PLACEHOLDER,
  };
}

export interface ListingGalleryMainImageProps {
  src: string;
  alt: string;
  className?: string;
}

export function ListingGalleryMainImage({ src, alt, className }: ListingGalleryMainImageProps) {
  return (
    <div
      data-testid="listing-gallery"
      className={cn('flex aspect-square w-full items-center justify-center bg-white', className)}
    >
      <img
        src={src}
        alt={alt}
        className="h-full w-full object-contain"
        data-testid="listing-gallery-main-image"
      />
    </div>
  );
}

export interface ListingGalleryThumbnailsProps {
  images: string[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  /** Pad to five slots with non-interactive placeholders (the panel only). */
  padToMinimumSlots?: boolean;
}

export function ListingGalleryThumbnails({
  images,
  selectedIndex,
  onSelect,
  padToMinimumSlots = false,
}: ListingGalleryThumbnailsProps) {
  const slotCount = padToMinimumSlots ? Math.max(images.length, MIN_SLOTS) : images.length;

  if (slotCount === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="listing-gallery-thumbnails">
      {Array.from({ length: slotCount }, (_, index) => {
        const url = images[index];

        // Empty slots are placeholders, never a stand-in photograph.
        if (!url) {
          return (
            <button
              key={`empty-${index}`}
              type="button"
              disabled
              aria-hidden="true"
              tabIndex={-1}
              data-testid={`listing-gallery-thumb-empty-${index}`}
              className="h-14 w-14 shrink-0 rounded-sm border border-border bg-muted/40"
            />
          );
        }

        const selected = index === selectedIndex;

        return (
          <button
            key={`${url}-${index}`}
            type="button"
            onClick={() => onSelect(index)}
            data-testid={`listing-gallery-thumb-${index}`}
            aria-current={selected}
            className={cn(
              'flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-sm border bg-white transition',
              selected ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary',
            )}
          >
            <img src={url} alt="" className="max-h-full max-w-full object-contain p-0.5" />
          </button>
        );
      })}
    </div>
  );
}
